import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';
import './studio-video.css';

const CANVAS_WIDTH = 720;
const CANVAS_HEIGHT = 1280;
const FPS = 30;

const effectFilters = {
  none: 'none',
  gray: 'grayscale(1)',
  warm: 'sepia(0.45) saturate(1.25) contrast(1.05)',
  punch: 'contrast(1.25) saturate(1.35)',
  light: 'brightness(1.18) contrast(1.05)',
  soft: 'blur(1.2px) brightness(1.05)'
};

const effectLabels = {
  none: 'Naturel',
  gray: 'Noir et blanc',
  warm: 'Chaud',
  punch: 'Contraste',
  light: 'Lumineux',
  soft: 'Doux'
};

const transitionLabels = {
  cut: 'Coupe nette',
  fade: 'Fondu doux',
  fadeblack: 'Fondu noir',
  zoom: 'Zoom doux'
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function fileKind(file) {
  if (file.type.startsWith('video/')) return 'video';
  return 'image';
}

function makeClip(file) {
  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    file,
    url: URL.createObjectURL(file),
    name: file.name,
    type: fileKind(file),
    duration: file.type.startsWith('video/') ? 6 : 4,
    trimStart: 0,
    effect: 'none',
    transition: 'cut',
    fit: 'cover',
    text: '',
    textColor: '#ffffff',
    textBg: '#0059B3'
  };
}

function getMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm'
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function drawContained(ctx, media, fit) {
  const sourceWidth = media.videoWidth || media.naturalWidth || media.width;
  const sourceHeight = media.videoHeight || media.naturalHeight || media.height;
  const sourceRatio = sourceWidth / sourceHeight;
  const targetRatio = CANVAS_WIDTH / CANVAS_HEIGHT;
  let drawWidth = CANVAS_WIDTH;
  let drawHeight = CANVAS_HEIGHT;

  if (fit === 'contain') {
    if (sourceRatio > targetRatio) {
      drawHeight = CANVAS_WIDTH / sourceRatio;
    } else {
      drawWidth = CANVAS_HEIGHT * sourceRatio;
    }
  } else if (sourceRatio > targetRatio) {
    drawWidth = CANVAS_HEIGHT * sourceRatio;
  } else {
    drawHeight = CANVAS_WIDTH / sourceRatio;
  }

  const x = (CANVAS_WIDTH - drawWidth) / 2;
  const y = (CANVAS_HEIGHT - drawHeight) / 2;
  ctx.drawImage(media, x, y, drawWidth, drawHeight);
}

function drawMultilineText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const test = line ? line + ' ' + word : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  lines.slice(0, 3).forEach((item, index) => {
    ctx.fillText(item, x, y + index * lineHeight);
  });
}
function drawWatermark(ctx) {
  const padding = 28;
  ctx.save();
  ctx.font = 'bold 34px Arial';
  ctx.textBaseline = 'middle';
  const label = 'IPTIC Dosso';
  const textWidth = ctx.measureText(label).width;
  const boxWidth = textWidth + 44;
  const boxHeight = 58;
  const x = CANVAS_WIDTH - boxWidth - padding;
  const y = padding;

  ctx.fillStyle = 'rgba(0, 89, 179, 0.78)';
  ctx.fillRect(x, y, boxWidth, boxHeight);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, x + 22, y + boxHeight / 2);
  ctx.fillStyle = '#26b56d';
  ctx.fillRect(x, y + boxHeight - 6, boxWidth, 6);
  ctx.restore();
}

function drawFrame(ctx, media, clip, title, subtitle = '') {
  ctx.save();
  ctx.fillStyle = '#070b14';
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  if (clip.fit === 'blur') {
    ctx.filter = 'blur(18px) brightness(0.72)';
    drawContained(ctx, media, 'cover');
    ctx.filter = effectFilters[clip.effect] || 'none';
    drawContained(ctx, media, 'contain');
  } else {
    ctx.filter = effectFilters[clip.effect] || 'none';
    drawContained(ctx, media, clip.fit);
  }
  ctx.filter = 'none';
  const gradient = ctx.createLinearGradient(0, CANVAS_HEIGHT - 360, 0, CANVAS_HEIGHT);
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(1, 'rgba(0,0,0,0.62)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, CANVAS_HEIGHT - 360, CANVAS_WIDTH, 360);
  if (clip.text) {
    ctx.font = 'bold 42px Arial';
    ctx.fillStyle = clip.textBg || '#0059B3';
    ctx.fillRect(34, CANVAS_HEIGHT - 320, CANVAS_WIDTH - 68, 148);
    ctx.fillStyle = clip.textColor || '#ffffff';
    drawMultilineText(ctx, clip.text, 54, CANVAS_HEIGHT - 276, CANVAS_WIDTH - 108, 44);
  }
  if (title) {
    ctx.font = 'bold 38px Arial';
    ctx.fillStyle = '#ffffff';
    drawMultilineText(ctx, title, 42, CANVAS_HEIGHT - 112, CANVAS_WIDTH - 84, 42);
  }
  if (subtitle) {
    ctx.font = '28px Arial';
    ctx.fillStyle = '#dbeafe';
    drawMultilineText(ctx, subtitle, 42, CANVAS_HEIGHT - 54, CANVAS_WIDTH - 84, 32);
  }
  drawWatermark(ctx);
  ctx.restore();
}

async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function loadVideo(url) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.onloadedmetadata = () => resolve(video);
    video.onerror = reject;
  });
}

export default function StudioVideo() {
  const [clips, setClips] = useState([]);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [target, setTarget] = useState('feed');
  const [rendering, setRendering] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [exportBlob, setExportBlob] = useState(null);
  const [exportUrl, setExportUrl] = useState('');
  const [progress, setProgress] = useState(0);
  const [muteOriginal, setMuteOriginal] = useState(true);
  const [aiText, setAiText] = useState('');
  const [serverRendering, setServerRendering] = useState(false);
  const [serverProgress, setServerProgress] = useState(0);
  const [serverMessage, setServerMessage] = useState('');
  const [serverJob, setServerJob] = useState(null);
  const [serverVideoUrl, setServerVideoUrl] = useState('');
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const clipsRef = useRef(clips);
  clipsRef.current = clips;
  useEffect(() => () => { clipsRef.current.forEach((clip) => URL.revokeObjectURL(clip.url)); }, []);
  const [selectedId, setSelectedId] = useState(null);
  const selectedClip = clips.find((clip) => clip.id === selectedId) || clips[0];
  const busy = rendering || serverRendering || publishing;

  useEffect(() => {
    if (!selectedClip) return undefined;
    let active = true;
    let frame;
    let media;
    const paint = () => {
      if (!active || !previewRef.current) return;
      drawFrame(previewRef.current.getContext('2d'), media, selectedClip, caption, subtitle);
      if (selectedClip.type === 'video') frame = requestAnimationFrame(paint);
    };
    const prepare = async () => {
      try {
        media = selectedClip.type === 'image'
          ? await loadImage(selectedClip.url) : await loadVideo(selectedClip.url);
        if (!active) return;
        if (selectedClip.type === 'video') {
          media.currentTime = Number(selectedClip.trimStart) || 0;
          media.onseeked = paint;
          media.onloadeddata = paint;
          if (media.readyState >= 2) paint();
        } else paint();
      } catch (_) { if (active) toast.error('Impossible de lire ce média'); }
    };
    prepare();
    return () => { active = false; cancelAnimationFrame(frame); if (media?.pause) media.pause(); };
  }, [selectedClip, caption, subtitle]);

  // Un changement de montage nécessite un nouvel export avant publication.
  useEffect(() => {
    setExportBlob(null);
    setExportUrl('');
    setServerJob(null);
    setServerVideoUrl('');
    setProgress(0);
    setServerProgress(0);
  }, [clips, audioFile, caption, subtitle, muteOriginal]);

  useEffect(() => () => { if (exportUrl) URL.revokeObjectURL(exportUrl); }, [exportUrl]);
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const API_BASE = config.apiBase;
  const VIDEO_API_BASE = config.videoStudioBase;
  const totalDuration = useMemo(() => {
    return clips.reduce((sum, clip) => sum + Number(clip.duration || 0), 0);
  }, [clips]);

  const handleClipSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const accepted = files.filter((file) => (
      file.type.startsWith('image/') || file.type.startsWith('video/')
    ));
    if (accepted.length !== files.length) {
      toast.error('Certains fichiers ne sont pas des images ou videos');
    }
    setClips((previous) => [...previous, ...accepted.map(makeClip)]);
    event.target.value = '';
  };

  const handleAudioSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      toast.error('Choisissez un fichier audio');
      return;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioFile(file);
    setAudioUrl(URL.createObjectURL(file));
  };

  const updateClip = (id, patch) => {
    setClips((previous) => previous.map((clip) => (
      clip.id === id ? { ...clip, ...patch } : clip
    )));
  };

  const removeClip = (id) => {
    setClips((previous) => {
      const clip = previous.find((item) => item.id === id);
      if (clip) URL.revokeObjectURL(clip.url);
      return previous.filter((item) => item.id !== id);
    });
  };

  const moveClip = (index, direction) => {
    setClips((previous) => {
      const next = [...previous];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return previous;
      const [item] = next.splice(index, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const clearExport = () => {
    if (exportUrl) URL.revokeObjectURL(exportUrl);
    setExportBlob(null);
    setExportUrl('');
  };

  const generateAiIdea = () => {
    const images = clips.filter((clip) => clip.type === 'image').length;
    const videos = clips.filter((clip) => clip.type === 'video').length;
    const tone = videos > images ? 'dynamique' : 'propre et narratif';
    const suggestion = [
      'Sc\u00e9nario propos\u00e9 : ouvrez avec le plan le plus fort,',
      'encha\u00eenez les moments importants, puis terminez par une image claire.',
      `Ton conseill\u00e9 : ${tone}.`,
      'L\u00e9gende : IPTIC Dosso vous pr\u00e9sente un moment fort de la vie \u00e9tudiante.',
      'Astuce : gardez la vid\u00e9o courte, entre 15 et 35 secondes.'
    ].join(' ');
    setAiText(suggestion);
    if (!caption.trim()) {
      setCaption('IPTIC Dosso - moment fort de la vie \u00e9tudiante');
    }
  };

  const renderImageClip = async (ctx, recorderStart, clip, title) => {
    const image = await loadImage(clip.url);
    const frames = Math.max(1, Math.round(Number(clip.duration) * FPS));
    for (let frame = 0; frame < frames; frame += 1) {
      drawFrame(ctx, image, clip, title, subtitle);
      setProgress(Math.min(98, Math.round((Date.now() - recorderStart) / (totalDuration * 1000) * 100)));
      await wait(1000 / FPS);
    }
  };

  const renderVideoClip = async (ctx, recorderStart, clip, title) => {
    const video = await loadVideo(clip.url);
    const duration = Math.max(1, Number(clip.duration || video.duration || 6));
    video.currentTime = Math.max(0, Number(clip.trimStart || 0));
    video.muted = true;
    await video.play().catch(() => null);
    const start = performance.now();
    while ((performance.now() - start) / 1000 < duration) {
      drawFrame(ctx, video, clip, title, subtitle);
      setProgress(Math.min(98, Math.round((Date.now() - recorderStart) / (totalDuration * 1000) * 100)));
      await wait(1000 / FPS);
    }
    video.pause();
  };

  const exportVideo = async () => {
    if (!clips.length) {
      toast.error('Ajoutez au moins une photo ou une vid\u00e9o');
      return;
    }
    if (!window.MediaRecorder) {
      toast.error('Ce navigateur ne supporte pas MediaRecorder');
      return;
    }

    clearExport();
    setRendering(true);
    setProgress(0);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const stream = canvas.captureStream(FPS);
      let audioElement = null;
      let audioContext = null;

      if (audioUrl) {
        audioElement = new Audio(audioUrl);
        audioElement.loop = true;
        audioContext = new AudioContext();
        const source = audioContext.createMediaElementSource(audioElement);
        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);
        destination.stream.getAudioTracks().forEach((track) => stream.addTrack(track));
        await audioContext.resume();
      }

      const chunks = [];
      const mimeType = getMimeType();
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 3500000
      });
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      const finished = new Promise((resolve) => {
        recorder.onstop = () => resolve();
      });

      recorder.start(250);
      const startTime = Date.now();
      if (audioElement) await audioElement.play().catch(() => null);

      for (const clip of clips) {
        if (clip.type === 'image') {
          await renderImageClip(ctx, startTime, clip, caption);
        } else {
          await renderVideoClip(ctx, startTime, clip, caption);
        }
      }

      recorder.stop();
      await finished;
      if (audioElement) audioElement.pause();
      if (audioContext) await audioContext.close();
      stream.getTracks().forEach((track) => track.stop());

      const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setExportBlob(blob);
      setExportUrl(url);
      setProgress(100);
      toast.success('Vid\u00e9o g\u00e9n\u00e9r\u00e9e avec la marque IPTIC Dosso');
    } catch (error) {
      console.error(error);
      toast.error('Erreur pendant le montage vid\u00e9o');
    } finally {
      setRendering(false);
    }
  };

  const pollServerJob = async (jobId) => {
    let keepPolling = true;
    while (keepPolling) {
      const token = localStorage.getItem('token');
      const response = await fetch(
        VIDEO_API_BASE + '/studio/job-status/' + encodeURIComponent(jobId),
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Suivi du rendu impossible');
      }
      const job = data.job;
      setServerJob(job);
      setServerProgress(Number(job.progress || 0));
      setServerMessage(job.message || 'Rendu serveur en cours');
      if (job.status === 'completed') {
        setServerVideoUrl(config.normalizeUrl(job.output_url));
        setServerRendering(false);
        toast.success('Montage serveur MP4 termin\u00e9');
        keepPolling = false;
      } else if (job.status === 'failed') {
        setServerRendering(false);
        throw new Error(job.message || 'Rendu serveur \u00e9chou\u00e9');
      } else {
        await wait(2500);
      }
    }
  };

  const startServerRender = async () => {
    if (!clips.length) {
      toast.error('Ajoutez au moins une photo ou une vid\u00e9o');
      return;
    }
    setServerRendering(true);
    setServerProgress(1);
    setServerMessage('Envoi des fichiers au serveur');
    setServerVideoUrl('');
    setServerJob(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      const settings = {
        caption,
        subtitle,
        target,
        mute_original: muteOriginal,
        clips: clips.map((clip) => ({
          duration: Number(clip.duration || 5),
          trimStart: Number(clip.trimStart || 0),
          effect: clip.effect,
          transition: clip.transition,
          fit: clip.fit,
          text: clip.text,
          textColor: clip.textColor,
          textBg: clip.textBg,
          type: clip.type,
          name: clip.name
        }))
      };
      formData.append('settings', JSON.stringify(settings));
      clips.forEach((clip) => formData.append('clips[]', clip.file));
      if (audioFile) formData.append('audio', audioFile);
      const response = await fetch(VIDEO_API_BASE + '/studio/create-job', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Impossible de lancer le rendu serveur');
      }
      setServerJob({ job_id: data.job_id, status: data.status, progress: 1 });
      setServerMessage(data.message || 'Montage serveur lanc\u00e9');
      toast.success('Rendu serveur Node lanc\u00e9');
      await pollServerJob(data.job_id);
    } catch (error) {
      setServerRendering(false);
      toast.error(error.message || 'Erreur du rendu serveur');
    }
  };
  const getPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

  const publishVideo = async () => {
    if (!exportBlob && serverJob?.status !== 'completed') {
      toast.error('G\u00e9n\u00e9rez la vid\u00e9o avant de publier');
      return;
    }

    setPublishing(true);
    try {
      const token = localStorage.getItem('token');

      if (!exportBlob && serverJob?.status === 'completed') {
        const pos = target === 'status' ? await getPosition() : null;
        const response = await fetch(VIDEO_API_BASE + '/studio/publish-job', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
          },
          body: JSON.stringify({
            job_id: serverJob.job_id,
            target,
            caption: caption || 'Vid\u00e9o cr\u00e9\u00e9e avec le Studio IPTIC Dosso',
            latitude: pos?.latitude || null,
            longitude: pos?.longitude || null,
            lieu: 'Studio IPTIC Dosso'
          })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.message || 'Publication impossible');
        toast.success(target === 'status'
          ? 'Vid\u00e9o publi\u00e9e dans Statuts'
          : 'Vid\u00e9o publi\u00e9e dans Actualit\u00e9s');
        return;
      }

      const file = new File(
        [exportBlob],
        'iptic-dosso-studio.webm',
        { type: exportBlob.type || 'video/webm' }
      );
      const formData = new FormData();
      const text = caption || 'Vid\u00e9o cr\u00e9\u00e9e avec le Studio IPTIC Dosso';

      if (target === 'feed') {
        formData.append('contenu', text);
        formData.append('type_media', 'video');
        formData.append('media_file', file);
        const response = await fetch(API_BASE + '/posts/create_post.php', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.message || 'Publication impossible');
        toast.success('Vid\u00e9o publi\u00e9e dans Actualit\u00e9s');
      } else {
        const pos = await getPosition();
        formData.append('contenu', text);
        formData.append('type_statut', 'video');
        formData.append('media', file);
        formData.append('lieu', 'Studio IPTIC Dosso');
        if (pos) {
          formData.append('latitude', pos.latitude);
          formData.append('longitude', pos.longitude);
        }
        const response = await fetch(API_BASE + '/statuts/create_statut.php', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.message || 'Publication impossible');
        toast.success('Vid\u00e9o publi\u00e9e dans Statuts');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur pendant la publication');
    } finally {
      setPublishing(false);
    }
  };
  const renderClipCard = (clip, index) => (
    <div key={clip.id} className={'studio-clip rounded-lg border border-gray-200 bg-white p-3 shadow-sm' + (selectedClip?.id === clip.id ? ' is-selected' : '')}>
      <button type="button" className="studio-select" onClick={() => setSelectedId(clip.id)} aria-pressed={selectedClip?.id === clip.id}>Plan {String(index + 1).padStart(2, '0')} <span>{selectedClip?.id === clip.id ? 'En cours de modification' : 'Voir dans l’aperçu'}</span></button>
      <div className="flex gap-3">
        <div className="h-24 w-16 flex-shrink-0 overflow-hidden rounded bg-gray-900">
          {clip.type === 'image' ? (
            <img src={clip.url} alt={'Aper\u00e7u'} className="h-full w-full object-cover" />
          ) : (
            <video src={clip.url} muted className="h-full w-full object-cover" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-800">{clip.name}</p>
          <p className="text-xs uppercase tracking-wide text-gray-400">{clip.type}</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="text-xs text-gray-500">
              Durée du plan (secondes)
              <input
                type="number"
                min="1"
                max="30"
                step="0.5"
                inputMode="decimal"
                value={clip.duration}
                onChange={(e) => updateClip(clip.id, { duration: Math.min(30, Math.max(1, Number(e.target.value) || 1)) })}
                className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
              />
              <small className="studio-help">Entre 1 et 30 secondes.</small>
            </label>
            <label className="text-xs text-gray-500">
              Cadrage
              <select
                value={clip.fit}
                onChange={(e) => updateClip(clip.id, { fit: e.target.value })}
                className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
              >
                <option value="cover">Remplir</option>
                <option value="contain">Afficher entier</option>
                <option value="blur">Fond flou</option>
              </select>
            </label>
          </div>
          <label className="mt-2 block text-xs text-gray-500">
            Effet
            <select
              value={clip.effect}
              onChange={(e) => updateClip(clip.id, { effect: e.target.value })}
              className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
            >
              {Object.entries(effectLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
          <fieldset className="studio-form-group mt-3 rounded-lg bg-slate-50 p-3">
            <legend>Découpage et transition</legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-500">
                Point de départ (secondes)
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={clip.trimStart || 0}
                  disabled={clip.type !== 'video'}
                  onChange={(e) => updateClip(clip.id, { trimStart: Math.max(0, Number(e.target.value) || 0) })}
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
                />
                <small className="studio-help">{clip.type === 'video' ? 'Ignorer le début de la vidéo.' : 'Disponible pour les vidéos.'}</small>
              </label>
              <label className="text-xs text-gray-500">
                Transition
                <select
                  value={clip.transition || 'cut'}
                  onChange={(e) => updateClip(clip.id, { transition: e.target.value })}
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
                >
                  {Object.entries(transitionLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
            </div>
            <p className="studio-help">Les transitions sont appliquées lors de l’export MP4.</p>
          </fieldset>
          <fieldset className="studio-form-group mt-3 rounded-lg bg-slate-50 p-3">
            <legend>Texte du plan</legend>
            <label className="mt-2 block text-xs text-gray-500">
              Message affiché sur ce plan (facultatif)
              <textarea
                rows={2}
                maxLength={120}
                value={clip.text || ''}
                onChange={(e) => updateClip(clip.id, { text: e.target.value })}
                placeholder="Titre, prix, slogan, explication..."
                className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
              />
              <small className="studio-help studio-counter">{(clip.text || '').length}/120 caractères</small>
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-500">
                Couleur du texte
                <input
                  type="color"
                  value={clip.textColor || '#ffffff'}
                  onChange={(e) => updateClip(clip.id, { textColor: e.target.value })}
                  className="mt-1 h-9 w-full rounded border border-gray-200"
                />
              </label>
              <label className="text-xs text-gray-500">
                Couleur du bandeau
                <input
                  type="color"
                  value={clip.textBg || '#0059B3'}
                  onChange={(e) => updateClip(clip.id, { textBg: e.target.value })}
                  className="mt-1 h-9 w-full rounded border border-gray-200"
                />
              </label>
            </div>
          </fieldset>
          <button type="button" className="studio-reset" onClick={() => updateClip(clip.id, { duration: clip.type === 'video' ? 6 : 4, trimStart: 0, effect: 'none', transition: 'cut', fit: 'cover', text: '', textColor: '#ffffff', textBg: '#0059B3' })}>Réinitialiser les réglages de ce plan</button>
      <div className="studio-clip-actions mt-3 flex justify-between gap-2">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => moveClip(index, -1)}
            disabled={index === 0}
            className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50"
          >
            Monter
          </button>
          <button
            type="button"
            onClick={() => moveClip(index, 1)}
            disabled={index === clips.length - 1}
            className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50"
          >
            Descendre
          </button>
        </div>
        <button
          type="button"
          onClick={() => removeClip(clip.id)}
          className="rounded bg-red-50 px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-100"
        >
          Supprimer
        </button>
      </div>
    </div>
  );

  return (
    <div className="studio-page min-h-screen p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <section className="studio-heading rounded-xl p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-green-700">
                IPTIC Dosso
              </p>
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                Votre histoire. Votre montage.
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-slate-500">
                Assemblez vos moments, ajustez chaque plan et partagez votre création.
              </p>
            </div>
            <div className="rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <strong>{clips.length} plans · {totalDuration || 0} s</strong><span className="block text-xs mt-1">Vertical 9:16 · 720 × 1280</span>
            </div>
          </div>
        </section>

        <fieldset disabled={busy} className="studio-workspace grid gap-5">
          <aside className="space-y-4">
            <section className="rounded-xl bg-white p-4 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900">01 — Vos médias</h2>
              <label className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 px-4 py-8 text-center text-blue-800 hover:bg-blue-100">
                <span className="font-semibold">Choisir photos ou vid&eacute;os</span>
                <span className="mt-1 text-xs">Images et vid&eacute;os locales</span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleClipSelect}
                  className="hidden"
                />
              </label>
              <label className="mt-3 block text-sm font-medium text-slate-700">
                Musique de fond (facultatif)
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioSelect}
                  className="studio-file mt-2 w-full rounded border border-slate-200 p-2 text-sm"
                />
              </label>
              {audioFile && (
                <div className="mt-2 text-xs text-green-700"><p className="truncate">Son ajouté : {audioFile.name}</p><audio controls src={audioUrl} className="mt-2 w-full" /><button type="button" className="mt-2 underline" onClick={() => { setAudioFile(null); setAudioUrl(''); }}>Retirer le son</button></div>
              )}
              <label className="studio-checkbox mt-3 flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={muteOriginal}
                  onChange={(e) => setMuteOriginal(e.target.checked)}
                />
                Enlever les sons originaux des clips
              </label>
            </section>

            <section className="rounded-xl bg-white p-4 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold text-slate-900">Une idée pour démarrer ?</h2>
                <button
                  type="button"
                  onClick={generateAiIdea}
                  className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                >
                  Proposer
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Des conseils pour structurer votre vidéo et préparer sa légende.
              </p>
              {aiText && (
                <div className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-900">
                  {aiText}
                </div>
              )}
            </section>
          </aside>

          <main className="space-y-4">
            <section className="rounded-xl bg-white p-4 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">02 — Montage</h2>
                <span className="text-xs text-slate-400">
                  Changez l'ordre, la dur&eacute;e, l'effet et le cadrage
                </span>
              </div>
              {clips.length > 0 && <nav className="studio-timeline" aria-label="Plans du montage">{clips.map((clip, index) => <button key={clip.id} type="button" aria-pressed={selectedClip?.id === clip.id} onClick={() => setSelectedId(clip.id)}><span>{String(index + 1).padStart(2, '0')}</span><strong>{clip.name}</strong><small>{clip.duration} s</small></button>)}</nav>}
              <div className="mt-4 space-y-3">
                {clips.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500">
                    Ajoutez des photos ou vid&eacute;os pour commencer le montage.
                  </div>
                ) : renderClipCard(selectedClip, clips.indexOf(selectedClip))}
              </div>
            </section>

            <section className="rounded-xl bg-white p-4 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900">03 — Texte et publication</h2>
              <p className="studio-help">Ces textes apparaissent sur tous les plans. La légende accompagne aussi la publication.</p>
              <label htmlFor="studio-caption" className="mt-3 block text-sm text-slate-600">Titre et légende (facultatif)</label>
              <textarea
                id="studio-caption"
                maxLength={180}
                aria-describedby="studio-caption-help"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows="3"
                placeholder="L&eacute;gende ou titre affich&eacute; dans la vid&eacute;o..."
                className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              <small id="studio-caption-help" className="studio-help studio-counter">{caption.length}/180 caractères · Privilégiez un titre court pour la vidéo.</small>
              <label htmlFor="studio-subtitle" className="mt-3 block text-sm text-slate-600">Sous-titre (facultatif)</label>
              <textarea
                id="studio-subtitle"
                maxLength={80}
                aria-describedby="studio-subtitle-help"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                rows="2"
                placeholder="Sous-titre optionnel affich&eacute; en bas de la vid&eacute;o..."
                className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none"
              />
              <small id="studio-subtitle-help" className="studio-help studio-counter">{subtitle.length}/80 caractères</small>
              <div className="studio-publish-fields mt-3 grid gap-3">
                <label className="text-sm text-slate-600">
                  Destination de la publication
                  <select
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  >
                    <option value="feed">Actualit&eacute;s</option>
                    <option value="status">Statuts</option>
                  </select>
                </label>
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={exportVideo}
                    disabled={rendering || serverRendering || clips.length === 0}
                    className="flex-1 rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
                  >
                    {rendering ? 'Montage...' : 'G\u00e9n\u00e9rer la vid\u00e9o'}
                  </button>
                  <button
                    type="button"
                    onClick={publishVideo}
                    disabled={publishing || (!exportBlob && serverJob?.status !== 'completed')}
                    className="flex-1 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {publishing ? 'Publication...' : 'Publier'}
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={startServerRender}
                disabled={rendering || serverRendering || clips.length === 0}
                className="mt-3 w-full rounded-lg border border-blue-300 px-4 py-2 font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {serverRendering ? 'Rendu serveur...' : 'Rendu serveur MP4'}
              </button>
              <p className="mt-2 text-xs text-slate-500">Export rapide : WebM, sans son original ni transitions. Pour les transitions et le son original, choisissez le rendu MP4.</p>
              {(rendering || progress > 0) && (
                <div className="mt-4">
                  <div role="progressbar" aria-label="Export vidéo" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full bg-blue-600 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Progression : {progress}%</p>
                </div>
              )}
              {(serverRendering || serverProgress > 0) && (
                <div className="mt-4">
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full bg-green-600 transition-all"
                      style={{ width: `${serverProgress}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Rendu serveur : {serverProgress}% {serverMessage ? '- ' + serverMessage : ''}
                  </p>
                </div>
              )}
            </section>
          </main>

          <aside className="space-y-4">
            <section className="studio-preview rounded-xl bg-white p-4 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900">Aper&ccedil;u vertical</h2>
              <div className="mx-auto mt-4 aspect-[9/16] w-full max-w-[260px] overflow-hidden rounded-[28px] bg-slate-950 p-2 shadow-xl">
                <div className="relative h-full overflow-hidden rounded-[22px] bg-black">
                  {selectedClip ? <canvas ref={previewRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} className="h-full w-full" aria-label={'Aperçu du plan : ' + selectedClip.name} /> : (
                    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-400">
                      L'aper&ccedil;u appara&icirc;t apr&egrave;s ajout de clips.
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                {selectedClip ? 'Aperçu du plan sélectionné avec ses effets et ses textes.' : 'Sélectionnez un média pour commencer.'}
              </p>
            </section>

            <section className="rounded-xl bg-white p-4 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900">R&eacute;sultat export&eacute;</h2>
              {(exportUrl || serverVideoUrl) ? (
                <div className="mt-3 space-y-3">
                  <video src={exportUrl || serverVideoUrl} controls className="w-full rounded-lg bg-black" />
                  <a
                    href={exportUrl || serverVideoUrl}
                    download={exportUrl ? 'iptic-dosso-studio.webm' : 'iptic-dosso-studio.mp4'}
                    className="block rounded-lg border border-blue-200 px-4 py-2 text-center text-sm font-semibold text-blue-700 hover:bg-blue-50"
                  >
                    T&eacute;l&eacute;charger la vid&eacute;o
                  </a>
                  <p className="text-xs text-slate-500">
                    {exportBlob ? `Taille : ${(exportBlob.size / (1024 * 1024)).toFixed(2)} Mo` : 'Rendu serveur MP4 pr\u00eat'}
                  </p>
                </div>
              ) : (
                <div className="mt-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-500">
                  G&eacute;n&eacute;rez la vid&eacute;o pour voir le r&eacute;sultat ici.
                </div>
              )}
            </section>
          </aside>
        </fieldset>
      </div>

      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="hidden"
      />
    </div>
  );
}
